import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Tabs, Tag, Typography, theme } from 'antd';
import type { ManagedCollectionDetailOut } from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { CoverThumb } from '@/components/common/show/CoverThumb';
import { StatCell } from '@/components/common/show/StatCell';
import { MANAGED_COLLECTION_SHOW_TABS } from '../constants';
import type { ManagedCollectionShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<ManagedCollectionShowTab, string> = {
  general: 'base.sections.basic_info',
  publications: 'user_catalog.common.tabs.publications',
  collection: 'base.sections.collection_info',
  search: 'user_catalog.common.tabs.search',
};

interface ManagedCollectionShowHeaderProps {
  record: ManagedCollectionDetailOut;
  activeTab: ManagedCollectionShowTab;
  onTabChange: (key: string) => void;
}

export const ManagedCollectionShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: ManagedCollectionShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <CoverThumb url={record.cover_url} size={128} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="baseline" wrap>
            {/* Long names wrap instead of pushing the tabs bar out. */}
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading3,
                overflowWrap: 'anywhere',
              }}
            >
              {record.name}
            </Title>
            <Tag>
              {t(`user_catalog.managed_collections.kind.${record.kind}`)}
            </Tag>
            {record.is_system && (
              <Tag color="gold">
                {t('user_catalog.managed_collections.fields.is_system')}
              </Tag>
            )}
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('base.fields.mod_status')}>
              <ModStatusTag value={record.mod_status} />
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={MANAGED_COLLECTION_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
